import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '../../components/feature/SiteHeader';
import SiteFooter from '../../components/feature/SiteFooter';
import ArticleCard from '../../components/feature/ArticleCard';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { getArticlesByCategory, getCategoryByKey } from '../../lib/content';

const PAGE_SIZE = 6;

type SortKey = 'latest' | 'hot';

export default function CategoryPage() {
  const { categoryName } = useParams<{ categoryName: string }>();
  const category = categoryName ? getCategoryByKey(categoryName) : undefined;
  const source = useMemo(
    () => (categoryName ? getArticlesByCategory(categoryName) : []),
    [categoryName],
  );

  const [sort, setSort] = useState<SortKey>('latest');
  const [tag, setTag] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const tagOptions = useMemo(() => {
    const set = new Set<string>();
    source.forEach((a) => a.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [source]);

  useEffect(() => {
    setSort('latest');
    setTag('all');
    setVisibleCount(PAGE_SIZE);
  }, [categoryName]);

  const filtered = useMemo(() => {
    let list = source;
    if (tag !== 'all') {
      list = list.filter((a) => a.tags.includes(tag));
    }
    const sorted = [...list];
    if (sort === 'hot') {
      sorted.sort((a, b) => b.views - a.views);
    } else {
      sorted.sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
    }
    return sorted;
  }, [source, tag, sort]);

  const visible = filtered.slice(0, visibleCount);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.ceil(visibleCount / PAGE_SIZE);
  const hasMore = visibleCount < filtered.length;

  const handleFilterChange = () => setVisibleCount(PAGE_SIZE);

  const resetFilters = () => {
    setSort('latest');
    setTag('all');
    setVisibleCount(PAGE_SIZE);
  };

  const changeSort = (key: SortKey) => {
    setSort(key);
    handleFilterChange();
  };

  const changeTag = (value: string) => {
    setTag(value);
    handleFilterChange();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full">
        {/* 分类头部 */}
        <section className="w-full px-4 md:px-8 pt-6 pb-8 border-b border-background-200 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category ? category.name : '未知分类' },
              ]}
            />
            {category ? (
              <div className="mt-5 flex items-start gap-4">
                <span className="shrink-0 w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-2xl">
                  <i className={category.icon} />
                </span>
                <div>
                  <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
                    {category.desc}
                  </p>
                </div>
              </div>
            ) : (
              <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">
                未知分类
              </h1>
            )}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8">
          <div className="max-w-6xl mx-auto">
            {!category ? (
              <div className="py-20 text-center">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300" />
                <p className="mt-4 text-foreground-600">该分类不存在或已下线</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-line" />
                  返回首页
                </Link>
              </div>
            ) : (
              <>
                {/* 筛选栏 */}
                <div className="flex flex-col gap-4 mb-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-foreground-500 mr-1">排序：</span>
                    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
                      {[
                        { key: 'latest' as SortKey, label: '最新' },
                        { key: 'hot' as SortKey, label: '最热' },
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => changeSort(item.key)}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            sort === item.key
                              ? 'bg-primary-500 text-background-50'
                              : 'text-foreground-600 hover:text-primary-600'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-foreground-500 mr-1">标签：</span>
                    <button
                      type="button"
                      onClick={() => changeTag('all')}
                      className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        tag === 'all'
                          ? 'bg-accent-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-700'
                      }`}
                    >
                      全部
                    </button>
                    {tagOptions.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => changeTag(t)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          tag === t
                            ? 'bg-accent-500 text-white'
                            : 'bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-700'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="text-sm text-foreground-500 mb-4">
                  共找到 <strong className="text-primary-600">{filtered.length}</strong> 条内容
                </p>

                {filtered.length === 0 ? (
                  <div className="py-20 text-center rounded-lg border border-background-200 bg-background-100">
                    <i className="ri-inbox-line text-4xl text-foreground-300" />
                    <p className="mt-4 text-foreground-600">暂无相关内容</p>
                    <p className="mt-1 text-sm text-foreground-500">
                      换个标签或排序方式试试看
                    </p>
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line" />
                      重置筛选
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {visible.map((article) => (
                        <ArticleCard key={article.id} article={article} variant="grid" />
                      ))}
                    </div>

                    {/* 分页 + 加载更多 */}
                    <div className="mt-8 flex flex-col items-center gap-5">
                      {hasMore && (
                        <button
                          type="button"
                          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                          className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-arrow-down-line" />
                          加载更多
                        </button>
                      )}

                      {totalPages > 1 && (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            disabled={currentPage <= 1}
                            onClick={() =>
                              setVisibleCount((c) => Math.max(PAGE_SIZE, c - PAGE_SIZE))
                            }
                            className="w-9 h-9 rounded-md flex items-center justify-center border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                            aria-label="上一页"
                          >
                            <i className="ri-arrow-left-s-line" />
                          </button>
                          {Array.from({ length: totalPages }).map((_, index) => {
                            const page = index + 1;
                            return (
                              <button
                                key={page}
                                type="button"
                                onClick={() => setVisibleCount(page * PAGE_SIZE)}
                                className={`w-9 h-9 rounded-md flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                                  currentPage === page
                                    ? 'bg-primary-500 text-background-50'
                                    : 'border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            disabled={currentPage >= totalPages}
                            onClick={() =>
                              setVisibleCount((c) =>
                                Math.min(totalPages * PAGE_SIZE, c + PAGE_SIZE),
                              )
                            }
                            className="w-9 h-9 rounded-md flex items-center justify-center border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                            aria-label="下一页"
                          >
                            <i className="ri-arrow-right-s-line" />
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}